import { DigitalDossierTaxInformationSerializer } from '@bcba/serializers';
import { FormSection } from '../../DigitalDossier/DigitalDossierSection/components/FormSection';
import { useTranslation } from '@bcba/react-hooks';
import { Grid, GridItem, ReadOnlyField } from '@agrotoken-tech/ui';
import { ReadOnlyDocumentSection } from './ReadOnlyDocumentSection';

type Props = {
  data: DigitalDossierTaxInformationSerializer;
};

export const ReadOnlyTaxInfoSection = ({ data }: Props) => {
  const { t } = useTranslation();
  const {
    cuitDocument,
    ivaCondition,
    bruteIncomeId,
    jurisdictions,
    iibbDocument,
    profitsId,
    profitsDocument,
    cm05Document,
    exemptionsDocument,
    additionalDocuments,
  } = data;

  return (
    <FormSection title={t('common.taxData')}>
      <Grid
        templateColumns={{ sm: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
        gap={6}
      >
        {cuitDocument ? (
          <ReadOnlyDocumentSection
            label={t(
              'backoffice.pages.taxInformation.cuitCertificateDocument.label',
            )}
            originalFileName={cuitDocument.document.originalFileName}
            documentUuid={cuitDocument.document.uuid}
            identifier={cuitDocument.document.identifier}
            expirationDate={cuitDocument.document.expirationDate}
          />
        ) : null}
        <ReadOnlyField
          label={t('backoffice.pages.taxInformation.IVACondition.label')}
          value={ivaCondition ? t(`ivaCondition.${ivaCondition}`) : '-'}
        />
        <GridItem />
        <ReadOnlyField
          label={t('backoffice.pages.taxInformation.IBB.label')}
          value={
            bruteIncomeId
              ? t(
                  `backoffice.pages.taxInformation.BRUTE_INCOME_TYPES.${bruteIncomeId}`,
                )
              : '-'
          }
        />
        <ReadOnlyField
          label={t('backoffice.pages.taxInformation.jurisdiction.label')}
          value={
            jurisdictions?.map((j) => j.province?.name ?? '-').join(', ') ?? '-'
          }
        />
        {iibbDocument ? (
          <ReadOnlyDocumentSection
            label={t('backoffice.pages.taxInformation.IBBCertificate.label')}
            originalFileName={iibbDocument.document.originalFileName}
            documentUuid={iibbDocument.document.uuid}
            identifier={iibbDocument.document.identifier}
            expirationDate={iibbDocument.document.expirationDate}
          />
        ) : null}
        <ReadOnlyField
          label={t('backoffice.pages.taxInformation.profitsTax.label')}
          value={
            profitsId
              ? t(`backoffice.pages.taxInformation.PROFIT_TYPES.${profitsId}`)
              : '-'
          }
        />
        {profitsDocument ? (
          <ReadOnlyDocumentSection
            label={t('backoffice.pages.taxInformation.profitsDocument.label')}
            originalFileName={profitsDocument.document.originalFileName}
            documentUuid={profitsDocument.document.uuid}
            identifier={profitsDocument.document.identifier}
            expirationDate={profitsDocument.document.expirationDate}
          />
        ) : null}
        {cm05Document ? (
          <ReadOnlyDocumentSection
            label={t('backoffice.pages.taxInformation.CM05Certificate.label')}
            originalFileName={cm05Document.document.originalFileName}
            documentUuid={cm05Document.document.uuid}
            identifier={cm05Document.document.identifier}
            expirationDate={cm05Document.document.expirationDate}
          />
        ) : null}
        {exemptionsDocument ? (
          <ReadOnlyDocumentSection
            label={t(
              'backoffice.pages.taxInformation.ExemptionsCertificate.label',
            )}
            originalFileName={exemptionsDocument.document.originalFileName}
            documentUuid={exemptionsDocument.document.uuid}
            identifier={exemptionsDocument.document.identifier}
            expirationDate={exemptionsDocument.document.expirationDate}
          />
        ) : null}
        {additionalDocuments?.map((doc, index) => {
          return (
            <ReadOnlyDocumentSection
              label={`${t(
                'backoffice.pages.taxInformation.additionalDocumentation.label',
              )} ${index + 1}`}
              originalFileName={doc.document.originalFileName}
              documentUuid={doc.document.uuid}
              identifier={doc.document.identifier}
              expirationDate={doc.document.expirationDate}
            />
          );
        }) ?? null}
      </Grid>
    </FormSection>
  );
};
